import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { usePluginState } from "../../usePluginState"
import { hideGlobalModal } from "runlevel/actions"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { useEffect, useState } from "react"
import { createLiveVideoFlight } from "features/pubsub/entityActions/flights/liveVideo"
import { selectUserID } from "user/profile/selectors"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { selectActivatedFireId } from "features/map/control/selectors"
import {
    selectActiveWorkspaceId,
    selectWorkspaceRegion,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { Input } from "@nova/ds-component-input"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { AlertContainer } from "@nova/component-alert-container"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { SupportedRegions } from "@nova/constants"
import { getApiGatewayDomainForRegion } from "@nova/configuration/browser"

export const flightIdContainer = { id: "" }

export const FlirMmaLiveStreamModal = () => {
    useEffect(() => {
        flightIdContainer.id = ""
    }, [])

    const { setSelectedPlugin } = usePluginState()
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)!
    const orgId = useAppSelector(selectActiveWorkspaceId())!
    const projectId = useAppSelector(selectActivatedFireId)!
    const region = useAppSelector(selectWorkspaceRegion()) || SupportedRegions.Canada
    const [step, setStep] = useState<"configure" | "success" | "error">("configure")

    const [layerName, setLayerName] = useState("")
    const [autoSave, setAutoSave] = useState(true)
    const [loading, setLoading] = useState(false)

    const close = () => {
        dispatch(hideGlobalModal())
        setSelectedPlugin(undefined)
    }

    const configureLivestream = async () => {
        setLoading(true)
        try {
            dispatch(
                createLiveVideoFlight({
                    data: {
                        createdBy: { userId },
                        name: layerName,
                        saveRecording: autoSave,
                        isMMA: true,
                    },
                })
            )

            const { url } = await fetchApiGateway(
                "post /org/:orgId/project/:projectId/flight/:flightId/get-mma-livestream-auth",
                {
                    params: { orgId, projectId, flightId: flightIdContainer.id },
                    body: { saveRecording: autoSave, region },
                    region: SupportedRegions.Canada,
                }
            )

            await navigator.clipboard.writeText(
                `-telemetry-url ${getApiGatewayDomainForRegion(SupportedRegions.Canada)}/org/${orgId}/project/${projectId}/flight/${flightIdContainer.id}/livestream-metadata ${url}`
            )

            dispatch(
                setGlobalNotificationContext({
                    status: GlobalNotificationStatus.success,
                    message: "Layer created. Stream link copied to clipboard",
                    timeoutInMs: 3000,
                })
            )

            setStep("success")
            close()
        } catch (err) {
            console.error(err)
            setStep("error")
        } finally {
            setLoading(false)
        }
    }

    return (
        <HighPriorityNovaModalContainer
            title="Start a Live Stream"
            className="w-[34rem]"
            canCloseModal
            closeModal={close}
            onReturn={() =>
                dispatch(
                    initializeCenterModalForComponentAction(ModalNames.ChooseLiveVideoTypeModal)
                )
            }
            bottomBar={
                <>
                    {step !== "success" && (
                        <div className="flex justify-end items-center w-full gap-2">
                            <ButtonTertiary title="Close" onClick={close} />{" "}
                            <ButtonPrimary
                                title="Create Layer"
                                onClick={configureLivestream}
                                disabled={loading}
                            />
                        </div>
                    )}
                </>
            }
        >
            {step !== "success" && (
                <div className="flex flex-col gap-6 py-4">
                    {step === "error" && (
                        <AlertContainer
                            type="error"
                            title="Something went wrong."
                            body="Please try again."
                        />
                    )}
                    <Input
                        value={layerName}
                        onChange={(e) => setLayerName(e.target.value)}
                        placeholder="Live Stream [Date]"
                        label="Stream Name"
                        autoComplete="off"
                    />
                    <div
                        className="flex gap-4 items-center cursor-pointer"
                        onClick={() => setAutoSave((prev) => !prev)}
                    >
                        <div className="text-gray-100">Auto Save Video</div>
                        <ToggleSwitch isToggled={autoSave} setIsToggled={() => {}} />
                    </div>
                </div>
            )}
        </HighPriorityNovaModalContainer>
    )
}
